import React, { useRef, useState, useCallback, useEffect } from 'react';

const WebcamCapture = ({ onCapture, onError }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null); // To keep track of the stream
  const [streaming, setStreaming] = useState(false);
  const [facingMode, setFacingMode] = useState('user'); // 'user' for front camera, 'environment' for back
  const [countDown, setCountDown] = useState(0);
  
  // Initialize webcam
  const initWebcam = useCallback(async () => {
    try {
      // If there's an existing stream, stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      const constraints = {
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: facingMode
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream; // Store the stream reference
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Use event listener instead of calling play directly
        videoRef.current.onloadedmetadata = () => {
          // Wrap in try-catch to handle any play interruptions
          try {
            const playPromise = videoRef.current.play();
            
            // Handle the play promise properly
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  // Play started successfully
                  setStreaming(true);
                })
                .catch(err => {
                  // Play was interrupted or failed
                  console.warn('Play was interrupted:', err);
                  // Don't set streaming to true if play failed
                });
            }
          } catch (err) {
            console.error('Error playing video:', err);
          }
        };
      }
    } catch (err) {
      console.error('Error accessing webcam:', err);
      onError('Unable to access webcam. Please make sure you have given camera permission and try again.');
    }
  }, [facingMode, onError]);
  
  // Clean up webcam resources when component unmounts
  useEffect(() => {
    return () => {
      // Stop any active countdown
      setCountDown(0);
      
      // Stop all tracks on the stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);
  
  // Start webcam when component mounts or facingMode changes
  useEffect(() => {
    initWebcam();
  }, [initWebcam]);
  
  // Capture image from webcam
  const captureImage = useCallback(() => {
    if (videoRef.current && canvasRef.current && streaming) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw video frame to canvas
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to data URL
      try {
        const imageDataUrl = canvas.toDataURL('image/jpeg');
        onCapture(imageDataUrl);
      } catch (err) {
        console.error('Error capturing image:', err);
        onError('Error capturing image from webcam. Please try again.');
      }
    }
  }, [streaming, onCapture, onError]);
  
  // Switch between front and back cameras
  const switchCamera = () => {
    setStreaming(false);
    setFacingMode(prevMode => prevMode === 'user' ? 'environment' : 'user');
  };
  
  // Handle countdown capture
  const startCountdown = () => {
    if (!streaming) return;
    
    setCountDown(3);
    
    const countInterval = setInterval(() => {
      setCountDown(prev => {
        if (prev <= 1) {
          clearInterval(countInterval);
          captureImage();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  return (
    <div className="card p-4">
      <h2 className="text-xl font-semibold mb-4 text-center">Webcam Capture</h2>
      
      <div className="relative bg-black rounded-lg overflow-hidden">
        <video 
          ref={videoRef} 
          className="w-full" 
          playsInline
          muted
          onCanPlay={() => setStreaming(true)}
        ></video>
        
        {countDown > 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <span className="text-white text-8xl font-bold">{countDown}</span>
          </div>
        )}
        
        <canvas ref={canvasRef} className="hidden"></canvas>
      </div>
      
      <div className="mt-4 flex space-x-2 justify-center">
        <button 
          onClick={startCountdown} 
          disabled={!streaming || countDown > 0}
          className="btn-primary flex items-center"
        >
          <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Take Photo
        </button>
        
        <button 
          onClick={switchCamera} 
          disabled={!streaming}
          className="btn-secondary flex items-center"
        >
          <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Switch Camera
        </button>
      </div>
      
      <p className="mt-4 text-sm text-center text-gray-500">
        Position your face in the center of the frame and look straight at the camera.
      </p>
    </div>
  );
};

export default WebcamCapture;